import { useState, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { loadTaxConfig } from '@/hooks/useTaxConfig';

/**
 * BranchTaxBadge — shows the country flag/code, currency, tax rate and authority
 * for a given branch, fed from the tenant's tax-config/effective endpoint.
 *
 * Use it next to any branch picker so users instantly see whether they are
 * entering a UAE invoice (5% FTA, AED) vs. a Saudi invoice (15% ZATCA, SAR).
 *
 * Props:
 *   branchId — required. Empty string falls back to tenant default.
 *   compact  — if true, renders a single inline pill instead of multi-chip.
 */
export default function BranchTaxBadge({ branchId, compact = false }) {
  const [eff, setEff] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // iter930 — use the shared session cache (dedups concurrent calls to
    // /accounting/tax-config/effective across all components).
    loadTaxConfig(branchId).then(data => { if (!cancelled) setEff(data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [branchId]);

  if (!eff) return null;

  const code = (eff.country_code || '').toUpperCase();
  const flag = code ? code.replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0))) : '🌍';
  const rateLabel = eff.vat_enabled ? `${eff.vat_rate}% ${eff.tax_label || 'VAT'}` : 'No tax';

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full bg-muted/60 border border-border text-foreground"
        data-testid="branch-tax-badge"
        title={`${eff.country_name} · ${eff.authority || ''} · ${rateLabel} · ${eff.currency}`}
      >
        <span>{flag}</span>
        <span className="font-medium">{code || 'GLOBAL'}</span>
        <span className="text-muted-foreground">·</span>
        <span className="font-mono">{eff.currency || ''}</span>
        <span className="text-muted-foreground">·</span>
        <span>{rateLabel}</span>
      </span>
    );
  }

  return (
    <div className="inline-flex items-center gap-2 flex-wrap" data-testid="branch-tax-badge">
      <span
        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-info/10 text-info border border-info/30 font-medium"
        title={eff.country_name}
      >
        <span>{flag}</span>
        <span>{eff.country_name}</span>
      </span>
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-muted/60 border border-border font-mono text-foreground">
        {eff.currency}
      </span>
      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium ${
        eff.vat_enabled ? 'bg-success/10 text-success border-success/30' : 'bg-muted/40 text-muted-foreground border-border'
      }`}>
        <Globe size={9} /> {rateLabel}
      </span>
      {eff.authority && (
        <span className="inline-flex text-[10px] px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200/40 font-medium">
          {eff.authority}
        </span>
      )}
      {eff.tax_number && (
        <span className="text-[10px] text-muted-foreground font-mono">{eff.trn_label || 'Tax #'}: {eff.tax_number}</span>
      )}
      {eff.reverse_charge_enabled && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-200/40 font-medium">
          Reverse Charge
        </span>
      )}
      {eff.source === 'branch_override' && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning/10 text-warning border border-warning/30 font-medium uppercase">
          Branch Override
        </span>
      )}
    </div>
  );
}
